import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1 }
  50%      { transform: scale(1.4); opacity: 0.55 }
`;

/**
 * Decorative SVG accent shown in the banner background — a circuit-style
 * graphic that gives the panel a "high-end ML platform" feel without
 * competing with the actual content.
 */
const CircuitArtwork = ({ palette = "navy" }) => {
  const stroke = palette === "navy" ? "rgba(125, 211, 252, 0.20)" : "rgba(125, 211, 252, 0.30)";
  const fill   = palette === "navy" ? "rgba(125, 211, 252, 0.18)" : "rgba(125, 211, 252, 0.30)";
  return (
    <Box
      component="svg"
      viewBox="0 0 360 200"
      preserveAspectRatio="xMidYMid slice"
      sx={{
        position: 'absolute',
        right: -20,
        top: -20,
        bottom: -20,
        width: 360,
        opacity: 1,
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="lsb-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* concentric orbital rings */}
      <circle cx="260" cy="100" r="80" fill="none" stroke={stroke} strokeWidth="1" strokeDasharray="2 6" />
      <circle cx="260" cy="100" r="56" fill="none" stroke={stroke} strokeWidth="1" />
      <circle cx="260" cy="100" r="32" fill="url(#lsb-core)" />

      {/* node dots */}
      {[
        [180, 100],
        [340, 100],
        [260,  20],
        [260, 180],
        [205,  45],
        [315, 155],
        [205, 155],
        [315,  45],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3" fill={fill} />
      ))}

      {/* connecting lines */}
      {[
        [180, 100, 260, 100],
        [260, 100, 340, 100],
        [260, 100, 260,  20],
        [260, 100, 260, 180],
      ].map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth="1" />
      ))}
    </Box>
  );
};

/**
 * State-driven banner that anchors the Launchpad page.
 *
 * - idle      → "Ready to launch" hero
 * - running   → "Training in progress" with shimmer
 * - paused    → "Training paused"
 * - completed → "Training completed" with subtle success palette
 *
 * Receives derived flags from the parent so it stays in lock-step with
 * the same state the rest of the page uses (no double-source of truth).
 */
const LaunchStatusBanner = ({ status = 'idle', currentConfig, progress = 0 }) => {
  const presets = {
    idle: {
      bg: 'linear-gradient(135deg, #1B365D 0%, #132643 60%, #0b1629 100%)',
      accent: '#7dd3fc',
      Icon: RocketLaunchIcon,
      kicker: 'Launchpad',
      title: currentConfig ? 'Ready to launch' : 'No configuration yet',
      body: currentConfig
        ? 'Your saved configuration is ready. Click Start training to coordinate the federated run across your selected client sites.'
        : 'Save a training configuration on the Training Setup page, then return here to launch your federated run.',
      footnote: currentConfig
        ? `Pipeline · ${currentConfig?.dataset_config?.processing_type || 'n/a'}`
        : 'No configuration saved yet.',
    },
    running: {
      bg: 'linear-gradient(135deg, #1B365D 0%, #0087B4 110%)',
      accent: '#a5f3fc',
      Icon: SyncRoundedIcon,
      kicker: 'In progress',
      title: 'Training in progress',
      body: 'The federated coordinator is broadcasting rounds to participating sites. Progress updates stream live below.',
      footnote: progress > 0 ? `${Math.round(progress)}% complete` : 'Starting…',
    },
    paused: {
      bg: 'linear-gradient(135deg, #1B365D 0%, #92400e 110%)',
      accent: '#fbbf24',
      Icon: PauseCircleFilledIcon,
      kicker: 'Paused',
      title: 'Training paused',
      body: 'The run is held. Resume to continue from the current round or stop to abort.',
      footnote: progress > 0 ? `${Math.round(progress)}% complete` : '',
    },
    completed: {
      bg: 'linear-gradient(135deg, #1B365D 0%, #166534 110%)',
      accent: '#86efac',
      Icon: CheckCircleIcon,
      kicker: 'Completed',
      title: 'Training completed',
      body: 'The federated run finished successfully. Detailed metrics and visualizations are available below.',
      footnote: 'View results in the Latest Training Results section below.',
    },
  };

  const cfg = presets[status] || presets.idle;
  const Icon = cfg.Icon;
  const isRunning = status === 'running';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        background: cfg.bg,
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.18)',
        mb: 2,
        minHeight: 124,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <CircuitArtwork palette="navy" />

      <Box sx={{ position: 'relative', zIndex: 1, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        {/* Icon — also serves as visual focal point */}
        <Box
          sx={{
            position: 'relative',
            width: 52,
            height: 52,
            borderRadius: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: cfg.accent,
            flexShrink: 0,
            backdropFilter: 'blur(6px)',
          }}
        >
          {/* Static icon — the single in-progress *spinner* lives on the Start
              button; here we use a calm pulse dot (below) so the page never
              shows two competing spinners at once. */}
          <Icon sx={{ fontSize: 26 }} />
          {isRunning && (
            <Box
              sx={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: cfg.accent,
                boxShadow: `0 0 8px ${cfg.accent}`,
                animation: `${pulseDot} 1.5s ease-in-out infinite`,
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: cfg.accent,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 0.25,
            }}
          >
            {cfg.kicker}
          </Typography>
          <Typography sx={{ color: '#ffffff', fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            {cfg.title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.8125rem', mt: 0.5, lineHeight: 1.5, maxWidth: 640 }}>
            {cfg.body}
          </Typography>
        </Box>

        {/* Right-side meta */}
        {cfg.footnote && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: cfg.accent,
              fontSize: '0.7rem',
              fontWeight: 600,
              fontFamily: 'var(--fx-font-mono)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.02em',
              alignSelf: 'flex-end',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: cfg.accent,
                boxShadow: `0 0 6px ${cfg.accent}`,
                animation: isRunning ? `${pulseDot} 1.5s ease-in-out infinite` : 'none',
              }}
            />
            {cfg.footnote}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LaunchStatusBanner;
